import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PharmacySelector } from "@/features/reports/pharmacy-selector";
import { ApiError, apiFetch, apiFetchBlob } from "@/lib/auth-api";
import { ensureElementVisible } from "@/lib/route-guards";
import { useDebounce } from "@/lib/use-debounce";

type OverrideKey =
	| "czyKorekta"
	| "nrKsef"
	| "idFakturyOryginalu"
	| "nrFakturyOryginalu"
	| "nrKsefOryginalu"
	| "dataWystawieniaOryginalu"
	| "podstawaKorekty"
	| "nrDokPrzedKor"
	| "idDokPrzedKor"
	| "dataWystawieniaPrzedKor"
	| "typFaktury"
	| "tytulFaktury"
	| "idKntKs";

type Overrides = Partial<Record<OverrideKey, string | null>> & { omitUnchanged?: boolean };

interface DostCandidate {
	id: number;
	kodhr: string | null;
	nazwa: string | null;
	miast: string | null;
	ostatniDokument: string | null;
	liczbaDokumentow: number;
}

interface DokfCandidate {
	id: number;
	symzr: string | null;
	dataw: string | null;
	iddost: number | null;
	ksnfv: string | null;
	kodhr: string | null;
}

interface KorFieldsResponse {
	isKor: boolean;
	invoiceNumber: string;
	current: Overrides;
	original: DokfCandidate | null;
	dostCandidates: DostCandidate[];
	dokfCandidates: DokfCandidate[];
}

/// Pola nagłówka korekty OSOZ-EDI, których działania w KS-AOW nie jesteśmy pewni.
/// `suggest` mówi, z jakiej listy podpowiedzi korzysta pole.
const FIELDS: {
	key: OverrideKey;
	label: string;
	hint: string;
	suggest?: "dost" | "dokfId" | "dokfNr" | "dokfData" | "dokfKsef" | "static";
	statics?: string[];
}[] = [
	{
		key: "czyKorekta",
		label: "czy-korekta",
		hint: "Dziś „Tak” (styl PGF). Wzorzec KS-EWD używa „T”.",
		suggest: "static",
		statics: ["T", "Tak"],
	},
	{
		key: "nrKsef",
		label: "nr-ksef",
		hint: "Elementu nie ma w oficjalnym XSD. Puste pole = nie wysyłaj.",
		suggest: "static",
		statics: [""],
	},
	{
		key: "idKntKs",
		label: "id-knt-ks sprzedawcy (kodhr)",
		hint: "Rekord dostawcy w kartotece apteki. Zdublowany dostawca to znany powód „nie znaleziono pierwotnego dokumentu”.",
		suggest: "dost",
	},
	{
		key: "idFakturyOryginalu",
		label: "id-faktury-oryginalu",
		hint: "Wg XSD to identyfikator oryginału w systemie wystawcy. Dziś wpisujemy dokf.id z bazy apteki, „0” = brak.",
		suggest: "dokfId",
	},
	{
		key: "nrFakturyOryginalu",
		label: "nr-faktury-oryginalu",
		hint: "Numer faktury pierwotnej z KSeF (NrFaKorygowanej).",
		suggest: "dokfNr",
	},
	{
		key: "nrKsefOryginalu",
		label: "nr-ksef-oryginalu",
		hint: "Numer KSeF oryginału. Puste, gdy oryginał sprzed KSeF.",
		suggest: "dokfKsef",
	},
	{
		key: "dataWystawieniaOryginalu",
		label: "data-wystawienia-oryginalu",
		hint: "Format RRRR-MM-DDT00:00:00.",
		suggest: "dokfData",
	},
	{
		key: "podstawaKorekty",
		label: "podstawa-korekty",
		hint: "Przyczyna korekty z KSeF.",
	},
	{
		key: "nrDokPrzedKor",
		label: "nr-dok-przed-kor",
		hint: "Pole XSD: dokument poprzedzający korektę (przy pierwszej korekcie = oryginał). Dziś nie wysyłamy.",
		suggest: "dokfNr",
	},
	{
		key: "idDokPrzedKor",
		label: "id-dok-przed-kor",
		hint: "Jak wyżej, identyfikator. Dziś nie wysyłamy.",
		suggest: "dokfId",
	},
	{
		key: "dataWystawieniaPrzedKor",
		label: "data-wystawienia-przed-kor",
		hint: "Jak wyżej, data. Dziś nie wysyłamy.",
		suggest: "dokfData",
	},
	{
		key: "typFaktury",
		label: "typ-faktury",
		hint: "Symbol dokumentu wg hurtowni. Wzorzec KS-EWD: FSK. Dziś nie wysyłamy.",
		suggest: "static",
		statics: ["FSK"],
	},
	{
		key: "tytulFaktury",
		label: "tytul-faktury",
		hint: "Wzorzec KS-EWD: „Korekta faktury VAT”. Dziś nie wysyłamy.",
		suggest: "static",
		statics: ["Korekta faktury VAT"],
	},
];

function asDataCzas(d: string | null): string {
	if (!d) return "";
	return d.includes("T") ? d : `${d}T00:00:00`;
}

export const Route = createFileRoute("/_auth/konfiguracja/ksef-osoz-edi")({
	component: AdminOsozEdiPage,
	beforeLoad: async ({ context, location }) => {
		await ensureElementVisible(
			context.queryClient,
			"sidebar.konfiguracja.ksef_osoz_edi",
			location.pathname,
		);
	},
});

interface AmbiguousCandidate {
	invoiceId: number;
	ksefReferenceNumber: string;
	issueDate: string | null;
	sellerName: string | null;
	buyerNip: string | null;
}

interface LookupCandidate {
	invoiceId: number;
	ksefReferenceNumber: string;
	ksefInvoiceNumber: string | null;
	issueDate: string | null;
	sellerName: string | null;
	buyerNip: string | null;
	buyerName: string | null;
	matchedIdfirm: number | null;
	suggestedIdfirm: number | null;
}

interface LookupResponse {
	candidates: LookupCandidate[];
}

function AdminOsozEdiPage() {
	const [query, setQuery] = useState("");
	const [idfirm, setIdfirm] = useState<number | null>(null);
	const [pharmacyName, setPharmacyName] = useState<string | null>(null);
	const [autoSelected, setAutoSelected] = useState(false);
	const [busy, setBusy] = useState(false);
	const [candidates, setCandidates] = useState<AmbiguousCandidate[] | null>(null);
	const [overrides, setOverrides] = useState<Overrides>({});

	const debouncedQuery = useDebounce(query.trim(), 400);

	const { data: lookup } = useQuery<LookupResponse>({
		queryKey: ["ksef-osoz-edi-lookup", debouncedQuery],
		queryFn: () =>
			apiFetch<LookupResponse>(
				`/ksef/admin/osoz-edi/lookup?query=${encodeURIComponent(debouncedQuery)}`,
			),
		enabled: debouncedQuery.length > 0,
		staleTime: 1000 * 30,
	});

	// Auto-fill picker when exactly 1 candidate with suggestion arrives.
	useEffect(() => {
		if (!lookup) {
			setAutoSelected(false);
			return;
		}
		if (lookup.candidates.length === 1) {
			const c = lookup.candidates[0];
			if (c.suggestedIdfirm != null && idfirm !== c.suggestedIdfirm) {
				setIdfirm(c.suggestedIdfirm);
				setPharmacyName(c.buyerName);
				setAutoSelected(true);
			}
		} else {
			setAutoSelected(false);
		}
	}, [lookup, idfirm]);

	const lookupCandidates = lookup?.candidates ?? [];

	const { data: korFields } = useQuery<KorFieldsResponse>({
		queryKey: ["ksef-osoz-edi-kor-fields", debouncedQuery, idfirm],
		queryFn: () =>
			apiFetch<KorFieldsResponse>(
				`/ksef/admin/osoz-edi/kor-fields?query=${encodeURIComponent(debouncedQuery)}&idfirm=${idfirm}`,
			),
		enabled: debouncedQuery.length > 0 && idfirm != null && lookupCandidates.length === 1,
		staleTime: 1000 * 30,
		retry: false,
	});
	const current = korFields?.current ?? {};

	// Nowa faktura lub apteka = czysty formularz, nadpisania z poprzedniej nie przechodzą.
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset celowo tylko przy zmianie klucza
	useEffect(() => {
		setOverrides({});
	}, [debouncedQuery, idfirm]);

	function fieldValue(key: OverrideKey): string {
		const o = overrides[key];
		if (o !== undefined && o !== null) return o;
		return current[key] ?? "";
	}
	function isChanged(key: OverrideKey): boolean {
		const o = overrides[key];
		return o !== undefined && o !== null && o !== (current[key] ?? "");
	}
	function setField(key: OverrideKey, value: string) {
		setOverrides({ ...overrides, [key]: value });
	}
	function resetField(key: OverrideKey) {
		const next = { ...overrides };
		delete next[key];
		setOverrides(next);
	}
	function suggestionsFor(f: (typeof FIELDS)[number]): { value: string; label: string }[] {
		if (!korFields) return [];
		const dokf = korFields.dokfCandidates;
		switch (f.suggest) {
			case "static":
				return (f.statics ?? []).map((v) => ({ value: v, label: v === "" ? "(nie wysyłaj)" : v }));
			case "dost":
				return korFields.dostCandidates
					.filter((d) => d.kodhr)
					.map((d) => ({
						value: d.kodhr ?? "",
						label: `${d.kodhr} · dost.id ${d.id} · ${d.miast ?? "—"} · ${d.liczbaDokumentow} dok., ostatni ${d.ostatniDokument ?? "—"}`,
					}));
			case "dokfId":
				return [
					{ value: "0", label: "0 (brak)" },
					...dokf.map((d) => ({
						value: String(d.id),
						label: `${d.id} · ${d.symzr ?? "—"} · ${d.dataw ?? "—"} · kodhr ${d.kodhr ?? "—"}`,
					})),
				];
			case "dokfNr":
				return dokf
					.filter((d) => d.symzr)
					.map((d) => ({ value: d.symzr ?? "", label: `${d.symzr} · ${d.dataw ?? "—"}` }));
			case "dokfKsef":
				return dokf
					.filter((d) => d.ksnfv)
					.map((d) => ({ value: d.ksnfv ?? "", label: `${d.ksnfv} · ${d.symzr ?? "—"}` }));
			case "dokfData":
				return dokf
					.filter((d) => d.dataw)
					.map((d) => ({
						value: asDataCzas(d.dataw),
						label: `${asDataCzas(d.dataw)} · ${d.symzr ?? "—"}`,
					}));
			default:
				return [];
		}
	}
	const changedCount =
		FIELDS.filter((f) => isChanged(f.key)).length + (overrides.omitUnchanged ? 1 : 0);

	function selectLookupCandidate(c: LookupCandidate) {
		setQuery(c.ksefReferenceNumber);
		if (c.suggestedIdfirm != null) {
			setIdfirm(c.suggestedIdfirm);
			setPharmacyName(c.buyerName);
			setAutoSelected(true);
		}
	}

	async function download(queryValue: string) {
		if (idfirm == null) {
			toast.error("Wybierz aptekę");
			return;
		}
		const queryTrim = queryValue.trim();
		if (!queryTrim) {
			toast.error("Podaj numer KSeF lub numer faktury");
			return;
		}

		setBusy(true);
		setCandidates(null);
		try {
			const params = new URLSearchParams({ query: queryTrim, idfirm: String(idfirm) });
			for (const f of FIELDS) if (isChanged(f.key)) params.set(f.key, fieldValue(f.key));
			if (overrides.omitUnchanged) params.set("omitUnchanged", "true");
			const { blob, response } = await apiFetchBlob(`/ksef/admin/osoz-edi?${params.toString()}`);
			const disposition = response.headers.get("content-disposition") ?? "";
			const match = disposition.match(/filename="?([^"]+)"?/);
			const filename = match?.[1] ?? `osoz_edi_${Date.now()}.xml`;
			const itemsTotal = Number(response.headers.get("x-osoz-items-total") ?? "0");
			const missingSeria = Number(response.headers.get("x-osoz-missing-seria") ?? "0");
			const missingExpiry = Number(response.headers.get("x-osoz-missing-expiry") ?? "0");
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			a.remove();
			URL.revokeObjectURL(url);
			toast.success(`Pobrano ${filename}`);
			if (itemsTotal > 0 && (missingSeria > 0 || missingExpiry > 0)) {
				const parts: string[] = [];
				if (missingSeria > 0) parts.push(`serii na ${missingSeria}/${itemsTotal} pozycjach`);
				if (missingExpiry > 0)
					parts.push(`daty waznosci na ${missingExpiry}/${itemsTotal} pozycjach`);
				toast.warning(
					`Dostawca nie podal ${parts.join(" oraz ")}. Uzupelnij recznie w KS-AOW po imporcie lub poproc dostawce o dodanie tych danych w fakturze KSeF.`,
					{ duration: 15000 },
				);
			}
		} catch (err) {
			if (err instanceof ApiError) {
				if (err.status === 409 && err.details && Array.isArray(err.details.candidates)) {
					setCandidates(err.details.candidates as AmbiguousCandidate[]);
					toast.message("Wiele faktur pasuje — wybierz konkretna pozycje");
				} else if (err.status === 403) {
					toast.error("Brak uprawnien (super_admin only)");
				} else if (err.status === 404) {
					toast.error("Nie znaleziono faktury KSeF dla tego zapytania");
				} else {
					toast.error(err.message ?? "Blad pobierania OSOZ-EDI");
				}
			} else if (err instanceof Error) {
				toast.error(err.message);
			} else {
				toast.error("Nieznany blad");
			}
		} finally {
			setBusy(false);
		}
	}

	return (
		<div className="container mx-auto py-8 max-w-3xl max-md:px-3 max-md:py-3">
			<Card>
				<CardHeader>
					{/* Telefon: tytul strony jest w pasku gornym. */}
					<CardTitle className="max-md:hidden">OSOZ-EDI (admin)</CardTitle>
					<CardDescription>
						Generuje OSOZ-EDI XML dla dowolnej faktury KSeF. Lookup po numerze KSeF (format{" "}
						<code>NIP-RRRRMMDD-XXXXXXXXXXXX-CC</code>) lub numerze faktury. Wymaga roli{" "}
						<strong>super_admin</strong>.
					</CardDescription>
				</CardHeader>
				{/* Enter w polach nie moze niczego uruchamiac: plik generuje tylko
				    przycisk „Pobierz OSOZ-EDI”, bo kazde pobranie to nowa probka. */}
				<CardContent
					className="space-y-4"
					onKeyDown={(e) => {
						if (e.key === "Enter" && (e.target as HTMLElement).tagName === "INPUT") {
							e.preventDefault();
						}
					}}
				>
					<div className="space-y-2">
						<Label htmlFor="osoz-query">Numer KSeF lub numer faktury</Label>
						<Input
							id="osoz-query"
							value={query}
							onChange={(e) => {
								setQuery(e.target.value);
								setAutoSelected(false);
							}}
							placeholder="5272643921-20260507-A1B2C3D4E5F6-7F lub FV/123/2026"
							disabled={busy}
						/>
						{lookupCandidates.length > 1 ? (
							<div className="mt-2 space-y-1.5 rounded-md border border-border p-2">
								<div className="text-xs text-muted-foreground">
									Znaleziono {lookupCandidates.length} faktur — kliknij aby wybrac:
								</div>
								<ul className="space-y-1">
									{lookupCandidates.map((c) => (
										<li key={c.invoiceId}>
											<button
												type="button"
												className="w-full text-left rounded-sm border border-border/40 px-2 py-1.5 hover:bg-muted/40"
												onClick={() => selectLookupCandidate(c)}
											>
												<div className="font-mono text-xs max-md:break-all">
													{c.ksefReferenceNumber}
												</div>
												<div className="text-xs text-muted-foreground">
													{c.issueDate ?? "—"} · {c.ksefInvoiceNumber ?? "—"} ·{" "}
													{c.sellerName ?? "—"}
													{c.buyerNip ? ` · NIP nabywcy ${c.buyerNip}` : ""}
												</div>
											</button>
										</li>
									))}
								</ul>
							</div>
						) : null}
					</div>
					<div className="space-y-2">
						<Label>Apteka</Label>
						<PharmacySelector
							value={idfirm}
							onChange={(unitcode, name) => {
								setIdfirm(unitcode);
								setPharmacyName(name);
								setAutoSelected(false);
							}}
						/>
						{autoSelected && pharmacyName ? (
							<div className="text-xs text-muted-foreground">
								Apteka wybrana automatycznie z faktury ({pharmacyName})
							</div>
						) : null}
					</div>
					{korFields?.isKor ? (
						<fieldset className="space-y-3 rounded-md border border-border/60 p-3">
							<legend className="px-1 text-sm font-medium">
								Nagłówek korekty {korFields.invoiceNumber}
								{changedCount > 0 ? ` · zmienionych pól: ${changedCount}` : ""}
							</legend>
							<p className="text-xs text-muted-foreground">
								Wartości poniżej to dokładnie to, co generator wpisze do pliku. Zmień pole albo
								kliknij podpowiedź, pobierz plik (nazwa dostanie sufiks _TEST) i wczytaj w KS-AOW.
								Produkcyjny plik dla aptek się nie zmienia.
								{korFields.original
									? ` Oryginał znaleziony w bazie apteki: dokf.id ${korFields.original.id}, dostawca dost.id ${korFields.original.iddost ?? "—"}.`
									: " Oryginału nie znaleziono w bazie apteki."}
							</p>
							{FIELDS.map((f) => {
								const sugg = suggestionsFor(f);
								const changed = isChanged(f.key);
								return (
									<div key={f.key} className="space-y-1">
										<div className="flex items-center gap-2 max-md:flex-wrap">
											<Label htmlFor={`ov-${f.key}`} className="font-mono text-xs">
												{f.label}
											</Label>
											{changed ? (
												<button
													type="button"
													className="text-xs text-muted-foreground underline"
													onClick={() => resetField(f.key)}
												>
													przywróć „{current[f.key] ?? ""}”
												</button>
											) : null}
										</div>
										<Input
											id={`ov-${f.key}`}
											value={fieldValue(f.key)}
											onChange={(e) => setField(f.key, e.target.value)}
											className={changed ? "border-primary" : undefined}
										/>
										<div className="text-xs text-muted-foreground">{f.hint}</div>
										{sugg.length > 0 ? (
											<div className="flex flex-wrap gap-1">
												{sugg.map((sg) => (
													<button
														key={`${f.key}-${sg.label}`}
														type="button"
														className="rounded-sm border border-border/60 px-1.5 py-0.5 text-xs hover:bg-muted/40 max-md:min-h-9 max-md:max-w-full max-md:break-words max-md:px-2 max-md:text-left"
														onClick={() => setField(f.key, sg.value)}
													>
														{sg.label}
													</button>
												))}
											</div>
										) : null}
									</div>
								);
							})}
							<label className="flex items-start gap-2 text-sm">
								<input
									type="checkbox"
									className="mt-1"
									checked={overrides.omitUnchanged ?? false}
									onChange={(e) => setOverrides({ ...overrides, omitUnchanged: e.target.checked })}
								/>
								<span>
									Pomiń pozycje bez zmiany
									<span className="block text-xs text-muted-foreground">
										Equalan wysyła w korekcie wszystkie wiersze faktury; zostają tylko te, gdzie
										ilość, cena lub wartość się zmieniła.
									</span>
								</span>
							</label>
						</fieldset>
					) : null}
					<Button
						type="button"
						className="max-md:h-11 max-md:w-full"
						onClick={() => download(query)}
						disabled={busy}
					>
						{busy ? "Generowanie…" : "Pobierz OSOZ-EDI"}
					</Button>

					{candidates && candidates.length > 0 ? (
						<div className="mt-6 space-y-2">
							<h3 className="text-sm font-medium">Wybierz konkretna fakture:</h3>
							<ul className="space-y-2">
								{candidates.map((c) => (
									<li key={c.invoiceId}>
										<button
											type="button"
											className="w-full text-left border border-border rounded-md p-3 hover:bg-muted/40"
											onClick={() => download(c.ksefReferenceNumber)}
										>
											<div className="font-mono text-xs max-md:break-all">
												{c.ksefReferenceNumber}
											</div>
											<div className="text-sm text-muted-foreground">
												{c.issueDate ?? "—"} · {c.sellerName ?? "—"}
												{c.buyerNip ? ` · NIP nabywcy ${c.buyerNip}` : ""}
											</div>
										</button>
									</li>
								))}
							</ul>
						</div>
					) : null}
				</CardContent>
			</Card>
		</div>
	);
}
